import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Debounced search across all phase columns in the All Phases view.
// Updates the `src` attribute on each turbo-frame target.
export default class extends Controller {
  static targets = ["input", "frame", "counter", "regionCheckbox", "regionToggleAll", "regionBadge", "sort", "filterBadge", "clearFiltersRow", "cardStyleButton"]
  static values  = { savedSort: String, savedRegions: Array, cardStyle: String, grouping: String, layout: String }

  connect() {
    this._debounceTimer = null
    // Initialize badges from server-rendered state
    this._updateRegionBadge()
    this._updateFilterBadge()
  }

  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._performSearch(), 300)
  }

  sort() {
    this._updateFilterBadge()
    this._performSearch()
  }

  // Compact ⇄ detailed card layout. Reloads every column so the server renders
  // the other partial; the choice is persisted server-side on page 1.
  setCardStyle(event) {
    const style = event.currentTarget.dataset.cardStyle
    if (!style || style === this.cardStyleValue) return

    this.cardStyleValue = style
    this._paintCardStyleButtons()

    // Keep the infinite-scroll controller's page 2+ requests in step.
    this.element.dataset.infiniteScrollCardStyleValue = style

    // user_role rides along as a registered super property.
    track("All Phases Card Style Changed", { card_style: style, source: "all_phases" })

    this._performSearch()
  }

  // Primary ↔ Secondary grouping (Phelipe 2026-09-03). The column set is
  // server-rendered, so this is a real navigation, not a frame refresh;
  // the server remembers the choice from the first frame it serves.
  setGrouping(event) {
    const grouping = event.currentTarget.dataset.grouping
    if (!grouping || grouping === this.groupingValue) return
    track("All Phases Grouping Changed", { grouping, source: "all_phases" })
    const url = new URL(window.location.href)
    url.searchParams.set("grouping", grouping)
    window.location.assign(url.toString())
  }

  // Kanban ⇄ list (CL4-6, Phelipe 2026-10-19). The page's structure differs, so
  // it's a real navigation; the server remembers the choice from the first frame.
  setLayout(event) {
    const layout = event.currentTarget.dataset.layout
    if (!layout || layout === this.layoutValue) return
    track("All Phases Layout Changed", { layout, source: "all_phases" })
    const url = new URL(window.location.href)
    url.searchParams.set("layout", layout)
    window.location.assign(url.toString())
  }

  _paintCardStyleButtons() {
    if (!this.hasCardStyleButtonTarget) return

    this.cardStyleButtonTargets.forEach((btn) => {
      const active = btn.dataset.cardStyle === this.cardStyleValue
      btn.setAttribute("aria-pressed", String(active))
      btn.classList.toggle("text-brand", active)
      btn.classList.toggle("text-text-muted", !active)
      btn.classList.toggle("hover:text-gray-700", !active)
    })
  }

  toggleAllRegions(event) {
    const checked = event.target.checked
    this.regionCheckboxTargets.forEach(cb => { cb.checked = checked })
    this._updateRegionBadge()
    this._updateFilterBadge()
    this._performSearch()
  }

  filterRegions() {
    const allChecked = this.regionCheckboxTargets.every(cb => cb.checked)
    const noneChecked = this.regionCheckboxTargets.every(cb => !cb.checked)

    if (this.hasRegionToggleAllTarget) {
      this.regionToggleAllTarget.checked = allChecked
      this.regionToggleAllTarget.indeterminate = !allChecked && !noneChecked
    }

    this._updateRegionBadge()
    this._updateFilterBadge()
    this._performSearch()
  }

  clearFilters() {
    // Reset sort to first option (default)
    if (this.hasSortTarget) {
      this.sortTarget.selectedIndex = 0
    }
    // Reset all region checkboxes to checked
    if (this.hasRegionToggleAllTarget) {
      this.regionToggleAllTarget.checked = true
    }
    this.regionCheckboxTargets.forEach(cb => { cb.checked = true })
    this._updateRegionBadge()
    this._updateFilterBadge()
    this._performSearch()
  }

  _updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return

    let count = 0
    // Count sort if not default (first option)
    if (this.hasSortTarget && this.sortTarget.selectedIndex > 0) count++
    // Count region filter if not all selected
    if (this._selectedRegions().length > 0) count++

    const badge = this.filterBadgeTarget
    if (count > 0) {
      badge.textContent = count
      badge.classList.remove("hidden")
      badge.classList.add("inline-flex")
    } else {
      badge.classList.add("hidden")
      badge.classList.remove("inline-flex")
    }

    // Show/hide clear row
    if (this.hasClearFiltersRowTarget) {
      this.clearFiltersRowTarget.classList.toggle("hidden", count === 0)
    }
  }

  _updateRegionBadge() {
    if (!this.hasRegionBadgeTarget) return

    const total = this.regionCheckboxTargets.length
    const checked = this.regionCheckboxTargets.filter(cb => cb.checked).length
    const badge = this.regionBadgeTarget

    if (checked < total && checked > 0) {
      badge.textContent = `${checked}/${total}`
      badge.classList.remove("hidden")
    } else {
      badge.classList.add("hidden")
    }
  }

  // Returns selected regions, or empty array if all are selected (meaning no filter)
  _selectedRegions() {
    if (!this.hasRegionCheckboxTarget) return []

    const all = this.regionCheckboxTargets
    const checked = all.filter(cb => cb.checked)

    // All checked = no filter
    if (checked.length === all.length) return []

    return checked.map(cb => cb.value)
  }

  _currentSort() {
    return this.hasSortTarget ? this.sortTarget.value : ""
  }

  _performSearch() {
    const term = this.inputTarget.value.trim()
    const regions = this._selectedRegions()
    const sort = this._currentSort()

    this.frameTargets.forEach((frame) => {
      const phase = frame.dataset.phase
      const base  = this._basePath(phase)
      const url   = new URL(base, window.location.origin)

      url.searchParams.set("phase", phase)
      url.searchParams.set("view", this._currentView())
      url.searchParams.set("page", "1")
      if (term) url.searchParams.set("search", term)
      if (sort) url.searchParams.set("sort", sort)
      if (this.cardStyleValue) url.searchParams.set("card_style", this.cardStyleValue)
      if (this.groupingValue) url.searchParams.set("grouping", this.groupingValue)
      if (this.layoutValue) url.searchParams.set("layout", this.layoutValue)
      regions.forEach(r => url.searchParams.append("regions[]", r))

      frame.src = url.toString()
    })
  }

  _basePath(phase) {
    return `/all_phases/all_phases_projects`
  }

  _currentView() {
    const params = new URLSearchParams(window.location.search)
    return params.get("view") || "open"
  }
}
